import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { api } from "@/lib/oraya-api";
import { Panel, LoadingState, EmptyState, StatusBadge, inputCls } from "@/command/ccUi";

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTerm(q);
    if (q.trim().length < 2) { setData({ total: 0, groups: [] }); return; }
    setLoading(true);
    api.get(`/cc/search?q=${encodeURIComponent(q)}`).then(({ data }) => setData(data)).catch(() => setData({ total: 0, groups: [] })).finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => { e.preventDefault(); if (term.trim()) navigate(`/command/search?q=${encodeURIComponent(term.trim())}`); };

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Search</h1>
      <p className="text-subtle mt-1 mb-6">Find customers, invoices, documents, people and more across ORAYA.</p>

      <form onSubmit={submit} className="relative mb-6 max-w-2xl">
        <SearchIcon className="w-4 h-4 text-subtle absolute left-4 top-1/2 -translate-y-1/2" />
        <input value={term} onChange={(e) => setTerm(e.target.value)} autoFocus data-testid="search-input"
          placeholder="Search ORAYA…" className={`${inputCls} pl-11 py-3`} />
      </form>

      {loading ? <LoadingState label="Searching…" /> : !data || (q.trim().length < 2) ? (
        <EmptyState title="Type at least 2 characters to search" />
      ) : data.total === 0 ? (
        <EmptyState title={`No results for “${q}”`} />
      ) : (
        <div className="flex flex-col gap-6" data-testid="search-results">
          <p className="text-sm text-subtle">{data.total} result(s) for “{q}”</p>
          {data.groups.map((g) => (
            <Panel key={g.type} title={g.type} testid={`search-group-${g.module}`}
              action={<Link to={g.link} className="text-xs text-subtle hover:text-ink inline-flex items-center gap-1">Open {g.type} <ArrowRight className="w-3 h-3" /></Link>}>
              <div className="flex flex-col gap-2">
                {g.items.map((it) => (
                  <Link key={it.id} to={it.link} data-testid={`search-item-${it.id}`}
                    className="flex items-center justify-between p-3 rounded-xl glass-medium hover:border-hairline/25 transition-colors">
                    <div><p className="text-ink text-sm">{it.title}</p>{it.subtitle && <p className="text-xs text-subtle mt-0.5">{it.subtitle}</p>}</div>
                    <StatusBadge status="info" label={g.type} />
                  </Link>
                ))}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
